import type { EditReceiptExtractionInput, ReceiptScanJob } from '@taler/api-client'
import {
  equalsMoney,
  formatLocalDate,
  type Money,
  parseLocalDate,
  requireCurrencyCode,
  sumMoney,
} from '@taler/domain'

let nextKey = 0
function newKey(): string {
  nextKey += 1
  return `line-${nextKey}`
}

export interface ReceiptLineItemDraft {
  readonly key: string
  description: string
  amount: Money | null
  quantity: number
  /** This line's own expense category - every line needs one of its own; see AGENTS.md's
   * receipt-scanning note on why there is no single whole-receipt category any more. */
  accountId: string | undefined
  /** The contact who bought this line: it posts to their account as a debt, not to `accountId`. */
  boughtBy: string | undefined
  /** A basket-level discount posts to a fixed discounts account regardless of `accountId`. */
  isDiscount: boolean
}

export interface ReceiptDraft {
  merchant: string
  receiptDate: string
  statedTotal: Money | null
  lineItems: ReceiptLineItemDraft[]
}

export function draftFromJob(job: ReceiptScanJob, contactAccountIds: ReadonlySet<string>): ReceiptDraft {
  return {
    merchant: job.merchant ?? '',
    receiptDate: job.receiptDate === undefined ? '' : formatLocalDate(job.receiptDate),
    statedTotal: job.statedTotal ?? null,
    lineItems: job.lineItems.map((item) => ({
      key: newKey(),
      description: item.description,
      amount: item.amount,
      quantity: item.quantity,
      ...splitLineTarget(item.accountId, contactAccountIds),
      isDiscount: item.isDiscount,
    })),
  }
}

/** A wire line target is either an expense category or a contact's account. */
export function splitLineTarget(
  accountId: string | undefined,
  contactAccountIds: ReadonlySet<string>,
): Pick<ReceiptLineItemDraft, 'accountId' | 'boughtBy'> {
  return accountId !== undefined && contactAccountIds.has(accountId)
    ? { accountId: undefined, boughtBy: accountId }
    : { accountId, boughtBy: undefined }
}

export function newLineItem(): ReceiptLineItemDraft {
  return {
    key: newKey(),
    description: '',
    amount: null,
    quantity: 1,
    accountId: undefined,
    boughtBy: undefined,
    isDiscount: false,
  }
}

function completeLineItems(draft: ReceiptDraft): readonly ReceiptLineItemDraft[] {
  return draft.lineItems.filter((item) => item.description.trim() !== '' && item.amount !== null)
}

/** `undefined` while the draft is incomplete (no items, or a line with no amount yet) -
 * distinct from `false`, which means every amount is in and the totals do not match. */
export function draftMismatch(draft: ReceiptDraft, currency: string): boolean | undefined {
  const total = draft.statedTotal
  if (total === null || draft.lineItems.length === 0) return undefined
  const amounts: Money[] = []
  for (const item of draft.lineItems) {
    if (item.amount === null) return undefined
    amounts.push(item.amount)
  }
  const sum = sumMoney(amounts, requireCurrencyCode(currency))
  return !equalsMoney(sum, total)
}

export function draftCanCommit(draft: ReceiptDraft, currency: string): boolean {
  const items = completeLineItems(draft)
  return (
    items.length > 0 &&
    items.every((item) => item.isDiscount || (item.boughtBy ?? item.accountId) !== undefined) &&
    draftMismatch(draft, currency) === false
  )
}

export function toEditInput(draft: ReceiptDraft, currency: string): EditReceiptExtractionInput {
  requireCurrencyCode(currency)
  return {
    counterAccountId: undefined,
    merchant: draft.merchant.trim() === '' ? undefined : draft.merchant.trim(),
    receiptDate: draft.receiptDate === '' ? undefined : parseLocalDate(draft.receiptDate),
    statedTotal: draft.statedTotal ?? undefined,
    lineItems: completeLineItems(draft).map((item) => ({
      description: item.description.trim(),
      amount: item.amount!,
      quantity: item.quantity,
      accountId: item.boughtBy ?? item.accountId,
      isDiscount: item.isDiscount,
    })),
  }
}
